import { memo } from "react";

/**
 * Premium 5-tab bottom navigation.
 * Equal spacing, equal touch targets, equal icon sizes — Apple-level alignment.
 * The Theo icon is a static SVG mini-orb that mirrors the V8 "living nebula in
 * glass" hero — same lavender→violet body, warm coral lower glow, white top
 * specular, ambient halo. One Theo identity across nav, home card, and hero.
 */
const ICON_SIZE = 22;

const tabs = [
  {
    id: "home",
    label: "Home",
    icon: (
      <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    id: "cycle",
    label: "Cycle",
    icon: (
      <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
  },
  {
    id: "theo",
    label: "Theo",
    // Theo identity icon — a static SVG mini-orb using the exact same
    // visual language as the hero TheoOrb: radial lavender gradient body,
    // warm coral lower glow, white top specular, ambient halo.
    // Recognizable at 24-28px as "that is Theo".
    icon: (
      <svg width={ICON_SIZE + 4} height={ICON_SIZE + 4} viewBox="0 0 24 24">
        <defs>
          <radialGradient id="theo-nav-body" cx="0.42" cy="0.40" r="0.62">
            <stop offset="0%"  stopColor="#FBF5FF" />
            <stop offset="22%" stopColor="#D8B4FE" />
            <stop offset="60%" stopColor="#A78BFA" />
            <stop offset="100%" stopColor="#6D28D9" />
          </radialGradient>
          <radialGradient id="theo-nav-warm" cx="0.50" cy="0.68" r="0.32">
            <stop offset="0%"   stopColor="#FDBA74" stopOpacity="0.85" />
            <stop offset="55%"  stopColor="#FB7185" stopOpacity="0.40" />
            <stop offset="100%" stopColor="#FB7185" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="theo-nav-halo" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%"   stopColor="#C4B5FD" stopOpacity="0.45" />
            <stop offset="60%"  stopColor="#C4B5FD" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#C4B5FD" stopOpacity="0" />
          </radialGradient>
        </defs>
        {/* Ambient halo */}
        <circle cx="12" cy="12" r="11.5" fill="url(#theo-nav-halo)" />
        {/* Glass sphere body */}
        <circle cx="12" cy="12" r="8.4" fill="url(#theo-nav-body)" />
        {/* Warm coral/amber glow lower-center */}
        <circle cx="12" cy="13.4" r="4.5" fill="url(#theo-nav-warm)" />
        {/* Top-left specular highlight */}
        <ellipse cx="9.6" cy="9" rx="2.0" ry="1.2" fill="rgba(255,255,255,0.85)" transform="rotate(-22 9.6 9)" />
        {/* Iridescent rim */}
        <circle cx="12" cy="12" r="8.4" fill="none" stroke="rgba(255,255,255,0.30)" strokeWidth="0.5" />
      </svg>
    ),
  },
  {
    id: "community",
    label: "Community",
    icon: (
      <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {/* Three-person community glyph — three heads, three shoulders,
            no speech bubbles, hearts, dots, connectors or fills. */}
        <circle cx="5"  cy="8"   r="2" />
        <circle cx="19" cy="8"   r="2" />
        <circle cx="12" cy="6.5" r="2.4" />
        <path d="M2 20v-1a3 3 0 0 1 6 0v1" />
        <path d="M16 20v-1a3 3 0 0 1 6 0v1" />
        <path d="M7 21v-2a5 5 0 0 1 10 0v2" />
      </svg>
    ),
  },
  {
    id: "care",
    label: "Care",
    icon: (
      <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z" />
      </svg>
    ),
  },
];

const TabButton = ({ tab, active, onClick }) => (
  <button
    className={`nav-tab${active ? " active" : ""}`}
    onClick={onClick}
    data-testid={`nav-${tab.id}`}
    id={`tab-${tab.id}`}
    style={{
      flex: 1, // EQUAL width
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      padding: "10px 0 8px",
      background: "transparent",
      border: "none",
      cursor: "pointer",
      color: active ? "#6D5EF5" : "#9CA3AF",
      transition: "color 0.18s ease",
      position: "relative",
      minWidth: 0,
    }}
  >
    {/* Active pill under the icon */}
    {active && (
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: 6,
          width: 22,
          height: 3,
          borderRadius: 2,
          background: "linear-gradient(90deg, #A78BFA, #7C3AED)",
        }}
      />
    )}
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        height: 26,
      }}
    >
      {tab.icon}
    </span>
    <span
      className="nav-label"
      style={{
        fontSize: 10.5,
        fontWeight: active ? 700 : 500,
        letterSpacing: "0.02em",
        lineHeight: 1,
      }}
    >
      {tab.label}
    </span>
  </button>
);

function BottomNav({ currentTab, onTabChange }) {
  return (
    <nav
      className="bottom-nav"
      data-testid="bottom-nav"
      style={{
        display: "flex",
        alignItems: "stretch",
        justifyContent: "space-between",
        padding: "0 4px",
      }}
    >
      {tabs.map((tab) => {
        // Legacy Care sub-tab aliases still count as Care being active.
        const isCareAlias = tab.id === 'care' && ['hormones', 'symptoms', 'insights'].includes(currentTab);
        return (
          <TabButton
            key={tab.id}
            tab={tab}
            active={currentTab === tab.id || isCareAlias}
            onClick={() => onTabChange(tab.id)}
          />
        );
      })}
    </nav>
  );
}

export default memo(BottomNav);
